"use client";

import { useMemo } from "react";
import { Button } from "@speakai/ui/button";
import { Input, Textarea } from "@speakai/ui/input";
import { Combobox } from "@/components/shared/combobox";
import { Badge } from "@speakai/ui/badge";
import { useTranslation } from "react-i18next";
import { ChevronRight, SkipForward, Wand2 } from "lucide-react";
import { MultiCombobox } from "@/components/shared/multi-combobox";
import type { MultiComboboxOption } from "@/components/shared/multi-combobox";
import { useFieldCreateAction } from "@/components/shared/fields/use-field-create-action";
import { SOURCE_TYPES } from "../utils";
import type { Field, GeneratedField } from "@/features/fields/api";

const AI_PROMPT_MAX = 500;
const AI_PROMPT_MIN = 10;

interface FolderDialogStepFieldsProps {
  sourceType: string;
  setSourceType: (value: string) => void;
  subCategory: string;
  setSubCategory: (value: string) => void;
  subCategoryOptions: { value: string; label: string }[];
  selectedFieldIds: Set<string>;
  setSelectedFieldIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  fieldsLoading: boolean;
  /** Fields suggested for the chosen source type. Rendered as the "Suggested" group. */
  suggestedFields: Field[];
  /** All other available fields. Rendered as the "Other" group. */
  otherFields: Field[];
  aiPrompt: string;
  setAiPrompt: (value: string) => void;
  aiPromptError: string | null;
  aiGenerating: boolean;
  aiGeneratedFields: GeneratedField[];
  setAiGeneratedFields: React.Dispatch<React.SetStateAction<GeneratedField[]>>;
  editingAiFieldIdx: number | null;
  setEditingAiFieldIdx: React.Dispatch<React.SetStateAction<number | null>>;
  editingAiFieldValue: string;
  setEditingAiFieldValue: (value: string) => void;
  handleGenerateFields: () => void | Promise<void>;
  handleStep2Skip: () => void;
  handleStep2Next: () => void | Promise<void>;
  updateFolderPending: boolean;
}

export function FolderDialogStepFields({
  sourceType,
  setSourceType,
  subCategory,
  setSubCategory,
  subCategoryOptions,
  selectedFieldIds,
  setSelectedFieldIds,
  fieldsLoading,
  suggestedFields,
  otherFields,
  aiPrompt,
  setAiPrompt,
  aiPromptError,
  aiGenerating,
  aiGeneratedFields,
  setAiGeneratedFields,
  editingAiFieldIdx,
  setEditingAiFieldIdx,
  editingAiFieldValue,
  setEditingAiFieldValue,
  handleGenerateFields,
  handleStep2Skip,
  handleStep2Next,
  updateFolderPending,
}: FolderDialogStepFieldsProps) {
  const { t } = useTranslation();
  const fieldCreate = useFieldCreateAction({
    onCreated: (field) => setSelectedFieldIds((prev) => new Set(prev).add(field.id)),
  });

  const sourceTypeLabel = SOURCE_TYPES.find((s) => s.value === sourceType)?.label;
  const suggestedGroupLabel = sourceTypeLabel
    ? `${t("FOLDERS.SUGGESTED_FOR")} ${sourceTypeLabel}`
    : t("FOLDERS.SUGGESTED_FOR");
  const otherGroupLabel = t("FOLDERS.OTHER_FIELDS");

  const fieldOptions = useMemo<MultiComboboxOption[]>(() => {
    const opts: MultiComboboxOption[] = [];
    suggestedFields.forEach((f) =>
      opts.push({
        value: f.id,
        label: f.name,
        description: f.type,
        group: suggestedGroupLabel,
      }),
    );
    otherFields.forEach((f) =>
      opts.push({
        value: f.id,
        label: f.name,
        description: f.type,
        // If there's no suggested group, render "Other" without the suggested header.
        // We still set a group so the visual is consistent when both exist.
        group: suggestedFields.length > 0 ? otherGroupLabel : undefined,
      }),
    );
    return opts;
  }, [suggestedFields, otherFields, suggestedGroupLabel, otherGroupLabel]);

  const selectedFieldValues = useMemo(
    () => Array.from(selectedFieldIds),
    [selectedFieldIds],
  );

  const handleFieldsChange = (next: string[]) => {
    setSelectedFieldIds(new Set(next));
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <label htmlFor="source-type" className="text-sm font-medium">
          Source Type
        </label>
        <Combobox
          id="source-type"
          placeholder="Select source type..."
          value={sourceType}
          options={SOURCE_TYPES.map((s) => ({ value: s.value, label: s.label }))}
          onChange={(val) => {
            setSourceType(val);
            setSubCategory("");
            setSelectedFieldIds(new Set());
            setAiGeneratedFields([]);
          }}
          hideSearch
        />
        {!sourceType && (
          <p className="text-xs text-muted-foreground mt-1">
            Select a source type to assign fields and proceed.
          </p>
        )}
      </div>

      {sourceType && subCategoryOptions.length > 0 && (
        <div className="flex flex-col gap-2">
          <label htmlFor="sub-category" className="text-sm font-medium">
            Sub-Category
          </label>
          <Combobox
            id="sub-category"
            placeholder="Select sub-category..."
            value={subCategory}
            options={subCategoryOptions.map((s) => ({ value: s.value, label: s.label }))}
            onChange={setSubCategory}
            hideSearch
          />
        </div>
      )}

      {sourceType && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Left: Field selection */}
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">Fields to assign</h4>
              {selectedFieldIds.size > 0 && (
                <Badge variant="secondary">
                  {selectedFieldIds.size} selected
                </Badge>
              )}
            </div>

            {fieldsLoading ? (
              <p className="text-xs text-muted-foreground">{t("FOLDERS.LOADING_FIELDS")}</p>
            ) : (
              <div data-sentry-mask>
                <MultiCombobox
                  value={selectedFieldValues}
                  onChange={handleFieldsChange}
                  options={fieldOptions}
                  placeholder="Select fields..."
                  searchPlaceholder={t("ACTION.SEARCH")}
                  selectAllVisible
                  aria-label="Fields to assign"
                  topAction={fieldCreate.topAction}
                />
              </div>
            )}
          </div>

          {/* Right: AI field generator */}
          <div className="flex flex-col gap-3" data-sentry-mask>
            <h4 className="text-sm font-medium">Generate fields with AI</h4>
            <Textarea
              placeholder="Describe what you want to capture from your recordings..."
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              rows={3}
              maxLength={AI_PROMPT_MAX}
            />
            {aiPromptError && (
              <p className="text-xs text-danger">{aiPromptError}</p>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleGenerateFields}
              disabled={
                aiGenerating ||
                !sourceType ||
                aiPrompt.trim().length < AI_PROMPT_MIN ||
                !!aiPromptError ||
                editingAiFieldIdx !== null
              }
            >
              <Wand2 className="h-4 w-4 mr-1" />
              {aiGenerating ? "Generating..." : "Generate Fields"}
            </Button>

            {aiGeneratedFields.length > 0 && (
              <div className="flex flex-col gap-1 max-h-40 overflow-y-auto rounded-lg border border-border p-3" data-sentry-mask>
                <p className="text-xs text-muted-foreground font-medium mb-1">
                  AI field suggestions
                </p>
                <p className="text-xs text-muted-foreground mb-2">
                  Use these as inspiration — create them in Settings to assign here.
                </p>
                {aiGeneratedFields.map((field, idx) =>
                  editingAiFieldIdx === idx ? (
                    <div key={idx} className="flex items-center gap-2 py-1 px-1">
                      <Input
                        autoFocus
                        value={editingAiFieldValue}
                        onChange={(e) => setEditingAiFieldValue(e.target.value)}
                        className="h-7 text-sm flex-1"
                        aria-label="Edit field name"
                      />
                      <button
                        type="button"
                        className="text-xs text-primary px-2 py-1.5 min-h-[32px]"
                        onClick={() => {
                          if (editingAiFieldValue.trim()) {
                            setAiGeneratedFields((prev) =>
                              prev.map((f, i) =>
                                i === idx ? { ...f, name: editingAiFieldValue.trim() } : f
                              )
                            );
                          }
                          setEditingAiFieldIdx(null);
                        }}
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        className="text-xs text-muted-foreground px-2 py-1.5 min-h-[32px]"
                        onClick={() => setEditingAiFieldIdx(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div
                      key={idx}
                      className="flex items-center justify-between py-1 px-1 rounded hover:bg-muted/50 group"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{field.name}</span>
                        <Badge variant="outline" className="text-xs">
                          {field.type}
                        </Badge>
                      </div>
                      <div className="flex opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity items-center gap-1">
                        <button
                          type="button"
                          aria-label={`Edit ${field.name}`}
                          className="text-xs text-muted-foreground hover:text-foreground px-2 py-1.5 min-h-[32px]"
                          onClick={() => {
                            setEditingAiFieldIdx(idx);
                            setEditingAiFieldValue(field.name);
                          }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          aria-label={`Remove ${field.name}`}
                          className="text-xs text-danger px-2 py-1.5 min-h-[32px]"
                          onClick={() => {
                            if (editingAiFieldIdx !== null) {
                              if (idx === editingAiFieldIdx) setEditingAiFieldIdx(null);
                              else if (idx < editingAiFieldIdx) setEditingAiFieldIdx(editingAiFieldIdx - 1);
                            }
                            setAiGeneratedFields((prev) => prev.filter((_, i) => i !== idx));
                          }}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  )
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex justify-between pt-2">
        <Button
          type="button"
          variant="ghost"
          onClick={handleStep2Skip}
        >
          <SkipForward className="h-4 w-4 mr-1" />
          Skip Fields
        </Button>
        <Button
          type="button"
          onClick={handleStep2Next}
          disabled={updateFolderPending}
        >
          {updateFolderPending ? "Saving..." : t("ACTION.NEXT")}
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>

      {fieldCreate.panel}
    </div>
  );
}
